import { useRequiredContext } from "@redotech/react-util/context";
import {
  getCarrierTrackingUrl,
  resolveCarrier,
} from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo, useMemo, useState } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getDeliveryDateStyles,
  getHeaderStyles,
  getLabelStyles,
  getLinkStyles,
  getTrackingNumberUrlStyles,
} from "../shared/block-styles";
import {
  CarrierBrandIcon,
  formatEventMessage,
  formatEventTimestamp,
  getShipmentStatusText,
} from "./tracking-details-shared";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const TrackingDetailsMobile = memo(function TrackingDetailsMobile({
  shipment,
  order,
  pickupConfig,
  stalledDays,
  optionalSubtext,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
  optionalSubtext: string | undefined;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);
  const deliveryDateStyles = getDeliveryDateStyles(pageSettings);
  const trackingUrlStyles = getTrackingNumberUrlStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const isDelivered =
    shipment?.status === OrderTrackingShipmentStatus.DELIVERED;
  const deliveryDate = isDelivered
    ? shipment?.deliveredAt
    : shipment?.estimatedDeliveryAt;
  const eddDistrusted = useMemo(() => {
    const resolved = shipment?.carrier
      ? resolveCarrier(shipment.carrier)
      : undefined;
    return !!(resolved?.found && resolved.definition.distrustDeliveryEstimate);
  }, [shipment?.carrier]);

  const trackingEvents = useMemo(
    () => (shipment?.trackingEvents?.length ? shipment.trackingEvents : []),
    [shipment?.trackingEvents],
  );
  const orderedEvents = useMemo(
    () =>
      [...trackingEvents].sort(
        (a, b) =>
          (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0),
      ),
    [trackingEvents],
  );

  const trackingCode = shipment?.trackingCode;
  const trackingUrl =
    getCarrierTrackingUrl(
      shipment?.carrier ?? undefined,
      trackingCode ?? undefined,
    ) ?? shipment?.trackingUrl;
  const showTrackingLink =
    !!trackingUrl && returnAppSettings.storeUrl !== "shopconzuri.myshopify.com";

  if (order.sourceName === "pos") {
    return null;
  }

  return (
    <Flex dir="column" gap="xl" p="3xl" w="full">
      <EstimatedDeliverySection
        bodyStyles={bodyStyles}
        date={deliveryDate ?? null}
        deliveryDateStyles={deliveryDateStyles}
        eddDistrusted={eddDistrusted}
        headerStyles={headerStyles}
        isDelivered={isDelivered}
        labelStyles={labelStyles}
      />

      <Flex dir="column" gap="xxs">
        <Text
          fontSize="xs"
          fontWeight="medium"
          style={labelStyles}
          textColor="tertiary"
        >
          Status
        </Text>
        <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
          {getShipmentStatusText({
            cancelReason: order.cancelReason,
            pickupConfig,
            shipment,
            source: order.sourceName,
            stalledDays,
          })}
        </Text>
      </Flex>

      {orderedEvents.length > 0 && (
        <CollapsibleTimeline
          bodyStyles={bodyStyles}
          events={orderedEvents}
          labelStyles={labelStyles}
          linkStyles={linkStyles}
        />
      )}

      {trackingCode && (
        <Flex dir="column" gap="xxs">
          <Text
            fontSize="xs"
            fontWeight="medium"
            style={labelStyles}
            textColor="tertiary"
          >
            Tracking
          </Text>
          <Flex align="center" gap="xs">
            <CarrierBrandIcon carrier={shipment?.carrier ?? null} />
            {showTrackingLink && trackingUrl ? (
              <ExternalLink
                showIcon={false}
                style={linkStyles}
                url={trackingUrl}
              >
                <Text
                  fontWeight="semibold"
                  style={{
                    ...trackingUrlStyles,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  textColor="secondary"
                >
                  {trackingCode}
                </Text>
              </ExternalLink>
            ) : (
              <Text
                fontWeight="semibold"
                style={{
                  ...bodyStyles,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                textColor="secondary"
              >
                {trackingCode}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {optionalSubtext && (
        <Text fontSize="sm" style={labelStyles}>
          {optionalSubtext}
        </Text>
      )}
    </Flex>
  );
});

const EstimatedDeliverySection = memo(function EstimatedDeliverySection({
  date,
  isDelivered,
  eddDistrusted,
  headerStyles,
  bodyStyles,
}: {
  date: Date | null;
  isDelivered: boolean;
  eddDistrusted: boolean;
  headerStyles: React.CSSProperties;
  bodyStyles: React.CSSProperties;
  labelStyles: React.CSSProperties;
  deliveryDateStyles: React.CSSProperties;
}) {
  const title = isDelivered ? "Delivered" : "Estimated delivery";

  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="xl" fontWeight="semibold" style={headerStyles}>
        {title}
      </Text>
      {date ? (
        <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
          {DAYS_OF_WEEK[date.getUTCDay()]}, {MONTHS[date.getUTCMonth()]}{" "}
          {date.getUTCDate()}
        </Text>
      ) : (
        <Text fontSize="md" style={bodyStyles}>
          {eddDistrusted ? "Unknown ETA" : "Awaiting ETA"}
        </Text>
      )}
    </Flex>
  );
});

const CollapsibleTimeline = memo(function CollapsibleTimeline({
  events,
  bodyStyles,
  labelStyles,
  linkStyles,
}: {
  events: ReturnType<typeof Array.prototype.slice>;
  bodyStyles: React.CSSProperties;
  labelStyles: React.CSSProperties;
  linkStyles: React.CSSProperties;
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleEvents = expanded ? events : events.slice(0, 1);
  const hiddenCount = events.length - 1;
  const dotColor = bodyStyles.color ?? "currentColor";

  return (
    <Flex dir="column" gap="md">
      {visibleEvents.map(
        (
          e: { timestamp?: Date; message: string; location?: unknown },
          i: number,
        ) => {
          const isLast = i === visibleEvents.length - 1;
          const isCollapsedStub = isLast && !expanded && hiddenCount > 0;
          const renderLine = !isLast || isCollapsedStub;
          // Latest entry (index 0) is the current step — hollow ring.
          // Older entries are completed — filled.
          const isCurrent = i === 0;

          return (
            <Flex
              dir="column"
              gap="xxs"
              key={i}
              style={{ position: "relative", paddingLeft: 24 }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: 16,
                  height: 16,
                  borderRadius: 16,
                  border: `2px solid ${dotColor}`,
                  boxSizing: "border-box",
                }}
              />
              {!isCurrent && (
                <span
                  style={{
                    position: "absolute",
                    left: 3,
                    top: 3,
                    width: 10,
                    height: 10,
                    borderRadius: 12,
                    backgroundColor: dotColor,
                  }}
                />
              )}
              {renderLine && (
                <span
                  style={{
                    position: "absolute",
                    left: 7,
                    top: 16,
                    width: 2,
                    height: isCollapsedStub ? 24 : "calc(100% - 8px)",
                    backgroundColor: dotColor,
                    borderRadius: 16,
                  }}
                />
              )}
              <Text fontSize="xs" fontWeight="semibold" style={bodyStyles}>
                {formatEventTimestamp(e.timestamp)}
              </Text>
              <Text fontSize="xs" style={labelStyles}>
                {formatEventMessage(e)}
              </Text>
            </Flex>
          );
        },
      )}
      {hiddenCount > 0 && (
        <button
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginLeft: 24,
            alignSelf: "flex-start",
            padding: 0,
            background: "none",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
          type="button"
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            style={{ ...linkStyles, textDecoration: "underline" }}
          >
            {expanded ? "Hide tracking history" : "View tracking history"}
          </Text>
        </button>
      )}
    </Flex>
  );
});
